class LogoService {
    constructor(imageRepository) {
        /**
         * @type {ImageRepository}
         */
        this.imageRepository = imageRepository;
    }

    /**
     * Returns a base64 encoded logo to be printed on a receipt.
     * In order of preference:
     * - Base64 encoded current branch printable logo
     * - Base64 encoded company printable logo
     * - Nothing
     *
     * Note that it does not fallback to a non-printable logo because that may result in horrible output.
     *
     * @param {number|null} currentBranchID
     *
     * @return {Promise<string|null>}
     */
    async getPrintableLogoUrl(currentBranchID) {
        if (currentBranchID) {
            try {
                const image = await this.imageRepository.getBranchPrintableLogo(currentBranchID);
                return image.data;
            } catch (e) {
                // Probably no branch logo.
            }
        }

        try {
            const image = await this.imageRepository.getCompanyPrintableLogo();
            return image.data;
        } catch (e) {
            // Probably no company logo.
        }

        return null;
    }

    /**
     * When logging in, or when refreshing the currently logged in user, the API returns some
     * base64 encoded image strings in the response for the company and branches.
     * We want to pick those out and store them separately then remove those from the data so they don't
     * get stored in the user in localstorage.
     *
     * Note that the param is nota User model. It is the raw data from the API response.
     *
     * @param {object} userData
     */
    async extractLogosFromUserData(userData) {
        await this.imageRepository.clear();

        let promises = [];

        if (userData.companies && userData.companies.encodedPrintableLogo) {
            promises.push(
                this.imageRepository.storeCompanyPrintableLogo(
                    userData.companies.encodedPrintableLogo
                )
            );

            // Remove from the data to be stored.
            delete userData.companies.encodedPrintableLogo;
        }

        if (userData.branches) {
            userData.branches.forEach((branch) => {
                if (branch.encodedPrintableLogo) {
                    promises.push(
                        this.imageRepository.storeBranchPrintableLogo(
                            branch.branchID,
                            branch.encodedPrintableLogo
                        )
                    );

                    delete branch.encodedPrintableLogo;
                }
            });
        }

        await Promise.all(promises);
    }
}

module.exports = LogoService;
